/**
 * TeamViewCalendar Component
 * Displays installer schedules in multiple views: Outlook, Timeline, Day, Week, Month
 * Extracted from CalendarPage.js for better maintainability
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Users, ChevronLeft, ChevronRight, ChevronDown, Check, X } from "lucide-react";
import { StartPointModal } from "./index";

// Work hours configuration (defaults)
const DEFAULT_WORK_HOURS = {
  morning: { start: "08:30", end: "12:30" },
  afternoon: { start: "13:30", end: "17:30" },
  lunchBreak: { start: "12:30", end: "13:30", duration: 60 },
  lunchWindow: { start: "12:00", end: "14:00" },
};

// Helper to get installer's lunch break (custom or default)
const getInstallerLunchBreak = (installer) => {
  const lunchStart = installer?.lunch_start || DEFAULT_WORK_HOURS.lunchBreak.start;
  const lunchEnd = installer?.lunch_end || DEFAULT_WORK_HOURS.lunchBreak.end;
  
  // Calculate duration in minutes
  const [startH, startM] = lunchStart.split(':').map(Number);
  const [endH, endM] = lunchEnd.split(':').map(Number);
  const duration = (endH * 60 + endM) - (startH * 60 + startM);
  
  return {
    start: lunchStart,
    end: lunchEnd,
    duration: duration > 0 ? duration : 60,
    isCustom: !!(installer?.lunch_start && installer?.lunch_end)
  };
};

const TeamViewCalendar = ({
  // Data props
  selectedTeam,
  teamInstallers,
  appointments,
  // Date/View state
  currentDate,
  setCurrentDate,
  currentView,
  setCurrentView,
  outlookMode,
  setOutlookMode,
  // Filter state (lifted from parent for persistence)
  selectedInstallerIds,
  setSelectedInstallerIds,
  // Event handlers
  onAppointmentClick,
  // Override state
  dailyOverrides,
  setDailyOverrides,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedInstaller, setSelectedInstaller] = useState(null);
  const [showStartPointModal, setShowStartPointModal] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [showInstallerSelector, setShowInstallerSelector] = useState(false);

  // Initialize selected installers when team installers load (only if no selection exists)
  useEffect(() => {
    if (teamInstallers.length > 0 && selectedInstallerIds.length === 0) {
      setSelectedInstallerIds(teamInstallers.map(i => i.id));
    }
  }, [teamInstallers, selectedInstallerIds.length, setSelectedInstallerIds]);

  // Filter installers based on selection
  const displayedInstallers = teamInstallers.filter(i => selectedInstallerIds.includes(i.id));

  const toggleInstaller = (installerId) => {
    setSelectedInstallerIds(prev => {
      if (prev.includes(installerId)) {
        // Don't allow deselecting if it's the last one
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== installerId);
      } else {
        return [...prev, installerId];
      }
    });
  };

  const selectAllInstallers = () => {
    setSelectedInstallerIds(teamInstallers.map(i => i.id));
  };

  const deselectAllInstallers = () => {
    // Keep at least one selected
    if (teamInstallers.length > 0) {
      setSelectedInstallerIds([teamInstallers[0].id]);
    }
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "#22c55e";
      case "not_completed": return "#ef4444";
      case "to_reschedule": return "#f59e0b";
      default: return "#3b82f6";
    }
  };

  const getInterventionColor = (interventionType) => {
    switch (interventionType) {
      case "installation": return "#3B82F6";
      case "cq": return "#22C55E";
      case "cq_pf": return "#F97316";
      case "ca": return "#EAB308";
      case "training": return "#A855F7";
      case "uninstall": return "#6B7280";
      default: return "#3B82F6";
    }
  };

  // Get appointment color based on status (matches legend: Confermato, Provvisorio, Eseguito, In Attesa Validazione, Non Eseguito, Da Rischedulare)
  const getAppointmentColor = (apt) => {
    const status = apt.status || "scheduled";
    if (status === "completed") return "#166534";          // Verde - Eseguito
    if (status === "pending_validation") return "#b45309"; // Arancione - In Attesa Validazione SF
    if (status === "not_completed") return "#991b1b";      // Rosso - Non Eseguito
    if (status === "to_reschedule") return "#f59e0b";      // Giallo - Da Rischedulare
    if (status === "provisional") return "#7e22ce";        // Viola - Provvisorio
    if (status === "confirmed") return "#2563eb";          // Blu - Confermato
    // Default: scheduled or other → use intervention type color
    return getInterventionColor(apt.intervention_type);
  };

  const getAppointmentBorder = (apt) => {
    const status = apt.status || "scheduled";
    if (status === "provisional") return "2px dashed #c084fc";
    if (status === "pending_validation") return "2px dashed #f59e0b";
    if (status === "to_reschedule") return "2px solid #f59e0b";
    if (status === "completed") return "2px solid #22c55e";
    if (status === "not_completed") return "2px solid #ef4444";
    if (status === "confirmed") return "2px solid #60a5fa";
    return "none";
  };

  // Get appointments for a specific installer on a specific date
  const getInstallerAppointments = (installerId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments
      .filter(apt => {
        const aptDate = new Date(apt.scheduled_date).toISOString().split('T')[0];
        return apt.assigned_installer_id === installerId && aptDate === dateStr;
      })
      .sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
  };

  // Calculate route info for an installer's daily appointments
  const calculateDailyRoute = (installer, date) => {
    const dayAppointments = getInstallerAppointments(installer.id, date);
    if (dayAppointments.length === 0) return null;

    const dateStr = date.toISOString().split('T')[0];
    const startAddress = dailyOverrides[dateStr]?.[installer.id] || installer.starting_address;
    
    const route = [];
    let prevAddress = startAddress;
    
    dayAppointments.forEach((apt, idx) => {
      const aptAddress = `${apt.address}, ${apt.city}, ${apt.province}`;
      route.push({
        from: prevAddress || 'Non impostato',
        to: aptAddress,
        appointment: apt,
        index: idx
      });
      prevAddress = aptAddress;
    });

    return { route, startAddress, appointments: dayAppointments };
  };

  // Navigate dates
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (currentView === 'outlook') {
      if (outlookMode === 'day') {
        newDate.setDate(newDate.getDate() + direction);
      } else {
        newDate.setDate(newDate.getDate() + (direction * 7));
      }
    } else if (currentView === 'timeline') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  // Get dates for current view
  const getViewDates = () => {
    const dates = [];
    if (currentView === 'day') {
      dates.push(new Date(currentDate));
    } else if (currentView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(d.getDate() + i);
        dates.push(d);
      }
    } else if (currentView === 'outlook') {
      // Mirrors OutlookView.viewDatesOutlook (line ~639): day = [currentDate];
      // week (Mon..Fri, 5 days) or fullweek (Mon..Sat, 6 days) starting Monday.
      if (outlookMode === 'day') {
        dates.push(new Date(currentDate));
      } else {
        const startOfWeek = new Date(currentDate);
        const dayOfWeek = startOfWeek.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(startOfWeek.getDate() + diff);
        const numDays = outlookMode === 'fullweek' ? 6 : 5;
        for (let i = 0; i < numDays; i++) {
          const d = new Date(startOfWeek);
          d.setDate(startOfWeek.getDate() + i);
          dates.push(d);
        }
      }
    } else if (currentView === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
    }
    return dates;
  };

  // Format header based on view
  const getHeaderTitle = () => {
    if (currentView === 'day') {
      return currentDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (currentView === 'week') {
      const viewDates = getViewDates();
      return `${viewDates[0].toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })} - ${viewDates[viewDates.length-1].toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (currentView === 'outlook') {
      if (outlookMode === 'day') {
        return currentDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      } else {
        const startOfWeek = new Date(currentDate);
        const dayOfWeek = startOfWeek.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(startOfWeek.getDate() + diff);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 4);
        return `${startOfWeek.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
    } else {
      return currentDate.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
    }
  };

  // Empty states
  if (teamInstallers.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="team-view-no-installers">
        <Users size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">{selectedTeam ? t('calendar.noInstallersInTeam') : 'Nessun installatore disponibile in alcun team visibile'}</p>
      </div>
    );
  }

  const viewDates = currentView === 'day' ? [currentDate] : getViewDates();

  // Unassigned OR ORPHAN appointments visible in current date range.
  // Fixes Issue 1 (Erika) + produzione ALD (Ciro Carotenuto): appuntamenti
  // assegnati a un installatore cancellato / non piu' presente in `teamInstallers`
  // (o senza installer_id) rimangono invisibili nella Vista Outlook (che disegna
  // solo colonne per installatori validi del team). Li mostriamo nel banner.
  const knownInstallerIds = new Set(teamInstallers.map(i => i.id));
  const rangeStart = viewDates[0];
  const rangeEnd = viewDates[viewDates.length - 1];
  const unassignedInRange = appointments.filter(apt => {
    if (apt.assigned_installer_id && knownInstallerIds.has(apt.assigned_installer_id)) return false;
    const aptDate = new Date(apt.scheduled_date);
    aptDate.setHours(0, 0, 0, 0);
    const s = new Date(rangeStart); s.setHours(0, 0, 0, 0);
    const e = new Date(rangeEnd); e.setHours(23, 59, 59, 999);
    return aptDate >= s && aptDate <= e;
  }).sort((a, b) => {
    const d = a.scheduled_date.localeCompare(b.scheduled_date);
    return d !== 0 ? d : (a.scheduled_time || '').localeCompare(b.scheduled_time || '');
  });

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="team-view-calendar">
      {/* Installer Selector */}
      <div className="p-3 border-b bg-blue-50">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setShowInstallerSelector(!showInstallerSelector)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            data-testid="installer-selector-toggle"
          >
            <Users size={18} />
            {t('calendar.selectInstallers')} ({selectedInstallerIds.length}/{teamInstallers.length})
            <ChevronDown size={16} className={`transition ${showInstallerSelector ? 'rotate-180' : ''}`} />
          </button>
          {showInstallerSelector && (
            <div className="flex gap-2">
              <button
                onClick={selectAllInstallers}
                className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                data-testid="select-all-installers"
              >
                {t('calendar.selectAll')}
              </button>
              <button
                onClick={deselectAllInstallers}
                className="text-xs px-2 py-1 bg-white border rounded hover:bg-gray-50"
                data-testid="deselect-all-installers"
              >
                {t('calendar.deselectAll')}
              </button>
            </div>
          )}
        </div>
        
        {showInstallerSelector && (
          <div className="flex flex-wrap gap-2 mt-2">
            {teamInstallers.map(installer => (
              <label
                key={installer.id}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition ${
                  selectedInstallerIds.includes(installer.id)
                    ? 'bg-lojack-red text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400'
                }`}
                data-testid={`installer-filter-${installer.id}`}
              >
                <input
                  type="checkbox"
                  checked={selectedInstallerIds.includes(installer.id)}
                  onChange={() => toggleInstaller(installer.id)}
                  className="sr-only"
                />
                <span className="text-sm font-medium">
                  {installer.first_name} {installer.last_name}
                </span>
                {selectedInstallerIds.includes(installer.id) && (
                  <Check size={14} />
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Unassigned / orphan appointments alert banner
          Fixes Issue 1 (Erika) + bug produzione (843 appts di CIRO CAROTENUTO, installatore
          cancellato): appuntamenti senza installatore O assegnati a un installer_id che non
          è più in `teamInstallers` (utente cancellato / cambiato team / ecc.) sono
          invisibili nella Vista Outlook. Li mostriamo in un banner in cima con click →
          apre il dettaglio per riassegnarli. */}
      {unassignedInRange.length > 0 && (
        <div className="p-3 border-b bg-amber-50 border-amber-200" data-testid="unassigned-appointments-banner">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <X size={16} className="text-amber-600 rotate-45" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {unassignedInRange.length} {unassignedInRange.length === 1 ? 'appuntamento non visibile' : 'appuntamenti non visibili'} in questo periodo
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Senza installatore, oppure assegnati a un tecnico non più presente. Clicca per riassegnarli.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {unassignedInRange.slice(0, 12).map(apt => (
                  <button
                    key={apt.id}
                    onClick={() => onAppointmentClick && onAppointmentClick(apt)}
                    className="text-xs px-2 py-1 bg-white border border-amber-300 rounded hover:bg-amber-100 hover:border-amber-500 transition"
                    data-testid={`unassigned-apt-${apt.id}`}
                    title={`${apt.client_name || ''} - ${apt.address || ''}`}
                  >
                    <span className="font-medium text-gray-700">
                      {new Date(apt.scheduled_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} {apt.scheduled_time}
                    </span>
                    <span className="text-gray-500 ml-1">
                      · {(apt.client_name || 'Senza cliente').substring(0, 24)}
                    </span>
                  </button>
                ))}
                {unassignedInRange.length > 12 && (
                  <span className="text-xs text-amber-700 self-center ml-1">
                    + altri {unassignedInRange.length - 12}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header with date navigation */}
      <div className={`p-4 border-b flex items-center justify-between ${currentView === 'outlook' ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gray-50'}`}>
        <button 
          onClick={() => navigateDate(-1)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
            currentView === 'outlook' 
              ? 'bg-white shadow-sm hover:shadow-md border border-gray-200 text-gray-700 hover:text-lojack-red' 
              : 'hover:bg-gray-200'
          }`}
          data-testid="nav-prev"
        >
          <ChevronLeft size={20} />
          {currentView === 'outlook' && (
            <span className="text-sm hidden sm:inline">
              {outlookMode === 'day' ? 'Giorno Prec.' : 'Sett. Prec.'}
            </span>
          )}
        </button>
        <div className="text-center flex items-center gap-3">
          <h3 className={`font-bold ${currentView === 'outlook' ? 'text-lg text-gray-800' : 'font-semibold text-gray-800'}`}>
            {getHeaderTitle()}
          </h3>
          {currentView === 'outlook' && (
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-3 py-1.5 bg-lojack-red text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition shadow-sm"
              data-testid="nav-today"
            >
              Oggi
            </button>
          )}
        </div>
        <button 
          onClick={() => navigateDate(1)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
            currentView === 'outlook' 
              ? 'bg-white shadow-sm hover:shadow-md border border-gray-200 text-gray-700 hover:text-lojack-red' 
              : 'hover:bg-gray-200'
          }`}
          data-testid="nav-next"
        >
          {currentView === 'outlook' && (
            <span className="text-sm hidden sm:inline">
              {outlookMode === 'day' ? 'Giorno Succ.' : 'Sett. Succ.'}
            </span>
          )}
          <ChevronRight size={20} />
        </button>
      </div>

      {/* OUTLOOK VIEW - Side by side installer calendars */}
      {currentView === 'outlook' && (
        <OutlookView 
          displayedInstallers={displayedInstallers}
          currentDate={currentDate}
          outlookMode={outlookMode}
          setOutlookMode={setOutlookMode}
          getInstallerAppointments={getInstallerAppointments}
          getAppointmentColor={getAppointmentColor}
          getAppointmentBorder={getAppointmentBorder}
          calculateDailyRoute={calculateDailyRoute}
          onAppointmentClick={onAppointmentClick}
          setRouteData={setRouteData}
          setShowRouteModal={setShowRouteModal}
        />
      )}

      {/* TIMELINE VIEW - Gantt style */}
      {currentView === 'timeline' && (
        <TimelineView
          displayedInstallers={displayedInstallers}
          currentDate={currentDate}
          getInstallerAppointments={getInstallerAppointments}
          getAppointmentColor={getAppointmentColor}
          getAppointmentBorder={getAppointmentBorder}
          onAppointmentClick={onAppointmentClick}
        />
      )}

      {/* Team grid - Day/Week/Month views */}
      {currentView !== 'timeline' && currentView !== 'outlook' && (
        <GridView
          displayedInstallers={displayedInstallers}
          currentView={currentView}
          viewDates={viewDates}
          currentDate={currentDate}
          i18n={i18n}
          getInstallerAppointments={getInstallerAppointments}
          getAppointmentColor={getAppointmentColor}
          getAppointmentBorder={getAppointmentBorder}
          calculateDailyRoute={calculateDailyRoute}
          onAppointmentClick={onAppointmentClick}
          setRouteData={setRouteData}
          setShowRouteModal={setShowRouteModal}
          t={t}
        />
      )}

      {/* Route Modal */}
      {showRouteModal && routeData && (
        <RouteModal
          routeData={routeData}
          i18n={i18n}
          t={t}
          getStatusColor={getStatusColor}
          onClose={() => setShowRouteModal(false)}
          onAppointmentClick={(apt) => {
            setShowRouteModal(false);
            onAppointmentClick(apt);
          }}
          onChangeStartPoint={(installer) => {
            setSelectedInstaller(installer);
            setShowStartPointModal(true);
          }}
        />
      )}

      {/* Start Point Override Modal */}
      {showStartPointModal && selectedInstaller && (
        <StartPointModal
          installer={selectedInstaller}
          date={currentDate}
          currentOverride={dailyOverrides[currentDate.toISOString().split('T')[0]]?.[selectedInstaller.id]}
          onClose={() => setShowStartPointModal(false)}
          onSave={(address) => {
            const dateStr = currentDate.toISOString().split('T')[0];
            setDailyOverrides(prev => ({
              ...prev,
              [dateStr]: {
                ...prev[dateStr],
                [selectedInstaller.id]: address
              }
            }));
            setShowStartPointModal(false);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// OUTLOOK VIEW COMPONENT
// ============================================================================
const OutlookView = ({
  displayedInstallers,
  currentDate,
  outlookMode,
  setOutlookMode,
  getInstallerAppointments,
  getAppointmentColor,
  getAppointmentBorder,
  calculateDailyRoute,
  onAppointmentClick,
  setRouteData,
  setShowRouteModal,
}) => {
  // When showing a SINGLE installer (typical for installer-role mobile view), make the panel full-width responsive
  const isSingleInstaller = displayedInstallers.length === 1;
  // Zoom hourHeight (Issue #2 Veronica 2026-07-03): consente di ingrandire/rimpicciolire
  // la vista Outlook. Persiste su localStorage.
  const [hourHeight, setHourHeightState] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('lojackOutlookHourHeight') || '48', 10);
      return [32, 48, 64, 96].includes(v) ? v : 48;
    } catch (e) { return 48; }
  });
  const setHourHeight = (v) => {
    setHourHeightState(v);
    try { localStorage.setItem('lojackOutlookHourHeight', String(v)); } catch (e) { /* ignore */ }
  };
  const zoomIn = () => {
    const levels = [32, 48, 64, 96];
    const i = levels.indexOf(hourHeight);
    if (i < levels.length - 1) setHourHeight(levels[i + 1]);
  };
  const zoomOut = () => {
    const levels = [32, 48, 64, 96];
    const i = levels.indexOf(hourHeight);
    if (i > 0) setHourHeight(levels[i - 1]);
  };
  return (
    <div className={`p-4 bg-gray-100 ${isSingleInstaller ? '' : 'overflow-x-auto'}`}>
      {/* Day/Week toggle */}
      <div className="mb-4 flex flex-col sm:flex-row items-center justify-center gap-3">
        <span className="text-sm font-medium text-gray-600">Visualizza per:</span>
        <div className="flex bg-white rounded-xl p-1.5 shadow-sm border border-gray-200">
          <button
            onClick={() => setOutlookMode('day')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
              outlookMode === 'day' 
                ? 'bg-lojack-red text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="outlook-mode-day"
          >
            <span>Singolo Giorno</span>
          </button>
          <button
            onClick={() => setOutlookMode('week')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
              outlookMode === 'week' 
                ? 'bg-lojack-red text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="outlook-mode-week"
          >
            <span>Settimana Lavorativa</span>
          </button>
          <button
            onClick={() => setOutlookMode('fullweek')}
            className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${
              outlookMode === 'fullweek' 
                ? 'bg-lojack-red text-white shadow-md' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="outlook-mode-fullweek"
          >
            <span>Settimana Intera</span>
          </button>
        </div>
        {/* Zoom controls (desktop only via CSS class) */}
        <div className="hidden sm:flex items-center gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-200">
          <button
            onClick={zoomOut}
            disabled={hourHeight === 32}
            className="px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-bold"
            data-testid="outlook-zoom-out"
            title="Riduci zoom"
          >
            −
          </button>
          <span className="text-xs text-gray-500 px-1 min-w-[42px] text-center font-mono" data-testid="outlook-zoom-level">
            {Math.round((hourHeight / 32) * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={hourHeight === 96}
            className="px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed font-bold"
            data-testid="outlook-zoom-in"
            title="Aumenta zoom"
          >
            +
          </button>
        </div>
      </div>
      
      <div className={`flex gap-4 ${isSingleInstaller ? 'flex-col' : ''}`} style={isSingleInstaller ? {} : { minWidth: 'max-content' }}>
        {displayedInstallers.map(installer => {
          let viewDatesOutlook;
          if (outlookMode === 'day') {
            viewDatesOutlook = [new Date(currentDate)];
          } else {
            const startOfWeek = new Date(currentDate);
            const dayOfWeek = startOfWeek.getDay();
            const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            startOfWeek.setDate(startOfWeek.getDate() + diff);
            
            const numDays = outlookMode === 'fullweek' ? 6 : 5;
            viewDatesOutlook = Array.from({ length: numDays }, (_, i) => {
              const date = new Date(startOfWeek);
              date.setDate(startOfWeek.getDate() + i);
              return date;
            });
          }

          const hours = Array.from({ length: 13 }, (_, i) => i + 7);
          const panelWidth = isSingleInstaller
            ? '100%'
            : (outlookMode === 'day' ? '280px' : outlookMode === 'fullweek' ? '440px' : '380px');
          const panelStyle = isSingleInstaller
            ? { width: '100%' }
            : { minWidth: panelWidth };

          return (
            <div key={installer.id} className="bg-white rounded-lg shadow-md overflow-hidden" style={panelStyle}>
              {/* Installer header */}
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{installer.first_name} {installer.last_name}</span>
                </div>
                <button 
                  onClick={() => {
                    const route = calculateDailyRoute(installer, currentDate);
                    if (route) {
                      setRouteData({ installer, ...route, date: currentDate });
                      setShowRouteModal(true);
                    }
                  }}
                  className="text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded"
                >
                  Percorso
                </button>
              </div>

              {/* Days header */}
              <div className="flex border-b bg-gray-50">
                <div className="w-12 p-1 border-r text-center text-xs text-gray-400 flex-shrink-0"></div>
                {viewDatesOutlook.map(date => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  // On single-installer view, columns must fit container WITHOUT horizontal overflow
                  // to keep the absolute appointment overlay aligned. We drop minWidth when isSingleInstaller.
                  const cellMinWidth = isSingleInstaller
                    ? undefined
                    : (outlookMode === 'day' ? '180px' : '60px');
                  return (
                    <div
                      key={date.toISOString()}
                      className={`flex-1 p-1 border-r text-center min-w-0 ${isToday ? 'bg-red-50' : ''}`}
                      style={cellMinWidth ? { minWidth: cellMinWidth } : undefined}
                    >
                      <p className={`text-xs uppercase ${isToday ? 'text-lojack-red font-bold' : 'text-gray-500'}`}>
                        {date.toLocaleDateString('it-IT', { weekday: 'short' })}
                      </p>
                      <p className={`text-sm font-bold ${isToday ? 'text-lojack-red' : 'text-gray-700'}`}>
                        {date.getDate()}
                      </p>
                      {outlookMode === 'day' && (
                        <p className="text-xs text-gray-400">
                          {date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Time grid */}
              <div className="relative" style={{ height: `${Math.min(hourHeight * 11 + 4, 600)}px`, overflowY: 'auto' }}>
                {hours.map(hour => (
                  <div key={hour} className="flex border-b" style={{ height: `${hourHeight}px` }}>
                    <div className="w-12 text-xs text-gray-400 text-right pr-1 border-r flex-shrink-0 flex items-center justify-end">
                      {hour}:00
                    </div>
                    {viewDatesOutlook.map(date => {
                      const cellMinWidth = isSingleInstaller
                        ? undefined
                        : (outlookMode === 'day' ? '180px' : '60px');
                      return (
                        <div
                          key={date.toISOString()}
                          className="flex-1 border-r border-gray-100 relative min-w-0"
                          style={cellMinWidth ? { minWidth: cellMinWidth } : undefined}
                        ></div>
                      );
                    })}
                  </div>
                ))}

                {/* Appointments overlay */}
                <div className="absolute top-0 left-12 right-0 bottom-0 pointer-events-none">
                  {/* Lunch break blocks - using installer's custom lunch time */}
                  {viewDatesOutlook.map((date, dayIndex) => {
                    const lunchBreak = getInstallerLunchBreak(installer);
                    const [lunchStartH, lunchStartM] = lunchBreak.start.split(':').map(Number);
                    const lunchStartDecimal = lunchStartH + (lunchStartM / 60);
                    const lunchTopPos = (lunchStartDecimal - 7) * hourHeight;
                    const lunchHeight = (lunchBreak.duration / 60) * hourHeight;
                    const numDays = viewDatesOutlook.length;
                    const leftPos = `calc(${dayIndex * (100/numDays)}% + 2px)`;
                    const width = `calc(${100/numDays}% - 4px)`;
                    
                    return (
                      <div
                        key={`lunch-${date.toISOString()}`}
                        className="absolute rounded text-xs pointer-events-none overflow-hidden border border-dashed border-gray-500"
                        style={{
                          top: `${lunchTopPos}px`,
                          left: leftPos,
                          width: width,
                          height: `${lunchHeight}px`,
                          backgroundColor: lunchBreak.isCustom ? '#059669' : '#6B7280',
                        }}
                        title={`Pausa Pranzo ${lunchBreak.start}-${lunchBreak.end}${lunchBreak.isCustom ? ' (personalizzata)' : ''}`}
                      >
                        <div className="p-0.5 h-full flex items-center justify-center">
                          <p className="text-white font-bold text-[9px] drop-shadow">
                            {outlookMode === 'day' ? (lunchBreak.isCustom ? `🍽️ ${lunchBreak.start}` : 'PAUSA') : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Appointments */}
                  {viewDatesOutlook.map((date, dayIndex) => {
                    const dayApts = getInstallerAppointments(installer.id, date);
                    const numDays = viewDatesOutlook.length;

                    // === OVERLAP LAYOUT (Outlook-style side-by-side) ===
                    // 1. Compute start/end minutes for each appointment.
                    // 2. Sort by start, then end.
                    // 3. Greedy column assignment: first column whose last apt ends <= this apt start.
                    // 4. Group into clusters of transitively overlapping apts; numCols = max(col)+1 per cluster.
                    const items = dayApts.map(apt => {
                      const [sh, sm] = (apt.scheduled_time || '00:00').split(':').map(Number);
                      let durationMin = apt.total_duration || apt.estimated_duration || 60;
                      if (apt.scheduled_end_time) {
                        const [eh, em] = apt.scheduled_end_time.split(':').map(Number);
                        const calc = (eh * 60 + em) - (sh * 60 + sm);
                        if (calc > 0) durationMin = calc;
                      }
                      const start = sh * 60 + sm;
                      return { apt, start, end: start + Math.max(durationMin, 15), durationMin };
                    }).sort((a, b) => a.start - b.start || a.end - b.end);

                    const colEnds = [];
                    items.forEach(it => {
                      let assigned = -1;
                      for (let i = 0; i < colEnds.length; i++) {
                        if (colEnds[i] <= it.start) { colEnds[i] = it.end; assigned = i; break; }
                      }
                      if (assigned === -1) { assigned = colEnds.length; colEnds.push(it.end); }
                      it.col = assigned;
                    });

                    // Cluster by transitive overlap
                    let clusterEnd = -1;
                    let cluster = [];
                    const finalize = () => {
                      if (!cluster.length) return;
                      const numCols = Math.max(...cluster.map(c => c.col)) + 1;
                      cluster.forEach(c => { c.numCols = numCols; });
                      cluster = [];
                    };
                    items.forEach(it => {
                      if (it.start >= clusterEnd) { finalize(); clusterEnd = it.end; }
                      cluster.push(it);
                      if (it.end > clusterEnd) clusterEnd = it.end;
                    });
                    finalize();

                    return items.map(({ apt, durationMin, col, numCols }) => {
                      const [startHour, startMin] = apt.scheduled_time.split(':').map(Number);
                      const topPos = ((startHour - 7) * hourHeight) + ((startMin / 60) * hourHeight);
                      const height = Math.max((durationMin / 60) * hourHeight, 24);
                      // Within a day column: split horizontally into numCols sub-columns
                      const dayLeftPct = dayIndex * (100 / numDays);
                      const dayWidthPct = 100 / numDays;
                      const subLeftPct = dayLeftPct + (col / numCols) * dayWidthPct;
                      const subWidthPct = dayWidthPct / numCols;
                      const leftPos = `calc(${subLeftPct}% + 2px)`;
                      const width = `calc(${subWidthPct}% - 3px)`;

                      return (
                        <div
                          key={apt.id}
                          onClick={() => onAppointmentClick(apt)}
                          className="absolute rounded text-xs cursor-pointer pointer-events-auto overflow-hidden shadow-sm hover:shadow-md hover:z-10 transition-all"
                          style={{
                            top: `${topPos}px`,
                            left: leftPos,
                            width: width,
                            height: `${height}px`,
                            backgroundColor: getAppointmentColor(apt),
                            border: getAppointmentBorder(apt),
                          }}
                          title={`${apt.scheduled_time}${apt.scheduled_end_time ? '-' + apt.scheduled_end_time : ''} - ${apt.client_name}${apt.city ? ' · ' + apt.city : ''}`}
                          data-testid={`outlook-apt-${apt.id}`}
                        >
                          <div className="p-0.5 h-full overflow-hidden">
                            <p className="text-white font-bold text-[10px] leading-tight truncate">
                              {apt.scheduled_time} {outlookMode === 'day' && numCols === 1 ? `- ${apt.client_name}` : ''}
                            </p>
                            {outlookMode === 'day' && numCols === 1 && height > 30 && (
                              <p className="text-white/80 text-[9px] leading-tight truncate">
                                {apt.address}, {apt.city}
                              </p>
                            )}
                            {outlookMode === 'day' && numCols === 1 && height > 50 && (
                              <p className="text-white/70 text-[9px] leading-tight">
                                {Math.floor(durationMin/60) > 0 ? `${Math.floor(durationMin/60)}h ` : ''}{durationMin % 60 > 0 ? `${durationMin % 60}min` : ''}
                              </p>
                            )}
                            {outlookMode === 'day' && numCols > 1 && height > 30 && (
                              <>
                                <p className="text-white/90 text-[9px] leading-tight truncate">
                                  {apt.client_name?.substring(0, 14)}
                                </p>
                                {apt.city && height > 45 && (
                                  <p className="text-white/70 text-[9px] leading-tight truncate">
                                    {apt.city.substring(0, 14)}
                                  </p>
                                )}
                              </>
                            )}
                            {outlookMode === 'week' && (
                              <>
                                <p className="text-white/90 text-[9px] leading-tight truncate">
                                  {apt.client_name?.substring(0, 10)}
                                </p>
                                {apt.city && height > 45 && (
                                  <p className="text-white/70 text-[9px] leading-tight truncate">
                                    {apt.city.substring(0, 10)}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// TIMELINE VIEW COMPONENT
// ============================================================================
const TimelineView = ({
  displayedInstallers,
  currentDate,
  getInstallerAppointments,
  getAppointmentColor,
  getAppointmentBorder,
  onAppointmentClick,
}) => {
  return (
    <div className="overflow-x-auto bg-white">
      {/* Timeline header with hours */}
      <div className="flex border-b bg-gray-50 sticky top-0 z-10">
        <div className="w-48 p-3 border-r font-medium text-gray-500 text-sm flex-shrink-0 bg-gray-50">
          Installatore
        </div>
        <div className="flex-1 flex">
          {Array.from({ length: 13 }, (_, i) => i + 7).map(hour => (
            <div key={hour} className="w-20 p-2 border-r text-center bg-gray-50 flex-shrink-0">
              <p className="font-medium text-gray-700 text-sm">{`${hour.toString().padStart(2, '0')}:00`}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Installer rows with timeline bars */}
      <div className="divide-y">
        {displayedInstallers.map(installer => {
          const dayApts = getInstallerAppointments(installer.id, currentDate);
          const lunchBreak = getInstallerLunchBreak(installer);
          const [lunchStartH, lunchStartM] = lunchBreak.start.split(':').map(Number);
          const lunchStartMinutes = (lunchStartH - 7) * 60 + lunchStartM;
          
          return (
            <div key={installer.id} className="flex hover:bg-gray-50/50">
              {/* Installer info */}
              <div className="w-48 p-3 border-r bg-white flex-shrink-0">
                <p className="font-semibold text-gray-800 truncate">
                  {installer.first_name} {installer.last_name}
                </p>
                <p className="text-xs text-gray-500">
                  {dayApts.length} appuntamenti
                </p>
                {lunchBreak.isCustom && (
                  <p className="text-xs text-green-600 mt-0.5">
                    🍽️ {lunchBreak.start}-{lunchBreak.end}
                  </p>
                )}
              </div>

              {/* Timeline bar area */}
              <div className="flex-1 relative h-20" style={{ minWidth: '1040px' }}>
                {/* Hour grid lines */}
                <div className="absolute inset-0 flex">
                  {Array.from({ length: 13 }, (_, i) => (
                    <div key={i} className="w-20 border-r border-gray-100 flex-shrink-0"></div>
                  ))}
                </div>

                {/* Lunch break indicator - using installer's custom lunch time */}
                <div
                  className="absolute top-1 bottom-1 rounded flex items-center justify-center border-2 border-dashed"
                  style={{
                    left: `${(lunchStartMinutes / 60) * 80}px`,
                    width: `${(lunchBreak.duration / 60) * 80}px`,
                    backgroundColor: lunchBreak.isCustom ? '#059669' : '#6B7280',
                    borderColor: lunchBreak.isCustom ? '#047857' : '#4B5563',
                  }}
                  title={`Pausa Pranzo ${lunchBreak.start}-${lunchBreak.end}${lunchBreak.isCustom ? ' (personalizzata)' : ''}`}
                >
                  <span className="text-white text-[11px] font-bold drop-shadow">
                    {lunchBreak.isCustom ? `🍽️ ${lunchBreak.start}` : 'PAUSA'}
                  </span>
                </div>

                {/* Appointment bars */}
                {dayApts.map(apt => {
                  const [startHour, startMin] = apt.scheduled_time.split(':').map(Number);
                  const startMinutes = (startHour - 7) * 60 + startMin;
                  const duration = apt.total_duration || apt.estimated_duration || 60;
                  
                  const leftPos = (startMinutes / 60) * 80;
                  const barWidth = Math.max((duration / 60) * 80, 60);
                  
                  return (
                    <div
                      key={apt.id}
                      onClick={() => onAppointmentClick(apt)}
                      className="absolute top-2 bottom-2 rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-all hover:scale-[1.02] flex flex-col justify-center px-2 overflow-hidden"
                      style={{
                        left: `${leftPos}px`,
                        width: `${barWidth}px`,
                        backgroundColor: getAppointmentColor(apt),
                        border: getAppointmentBorder(apt),
                      }}
                      title={`${apt.scheduled_time} - ${apt.client_name} (${duration} min)`}
                    >
                      <p className="text-white text-xs font-bold truncate">
                        {apt.scheduled_time}
                      </p>
                      <p className="text-white text-xs truncate opacity-90">
                        {apt.client_name}
                      </p>
                      {apt.installations && apt.installations.length > 1 && (
                        <p className="text-white text-[10px] truncate opacity-80">
                          {apt.installations.length} veicoli
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="p-4 border-t bg-gray-50">
        <p className="text-xs text-gray-500 mb-2 font-medium">Legenda durata:</p>
        <div className="flex gap-4 text-xs text-gray-600">
          <span>30 min = 40px</span>
          <span>1 ora = 80px</span>
          <span>2 ore = 160px</span>
          <span>3 ore = 240px</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// GRID VIEW COMPONENT (Day/Week/Month)
// ============================================================================
const GridView = ({
  displayedInstallers,
  currentView,
  viewDates,
  currentDate,
  i18n,
  getInstallerAppointments,
  getAppointmentColor,
  getAppointmentBorder,
  calculateDailyRoute,
  onAppointmentClick,
  setRouteData,
  setShowRouteModal,
  t,
}) => {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-max">
        {/* Date headers */}
        <div className="flex border-b bg-gray-50 sticky top-0 z-10">
          <div className="w-48 p-3 border-r font-medium text-gray-500 text-sm flex-shrink-0 bg-gray-50">
            {t('teams.installer')}
          </div>
          {currentView === 'day' ? (
            Array.from({ length: 12 }, (_, i) => i + 7).map(hour => (
              <div key={hour} className="w-28 p-2 border-r text-center bg-gray-50 flex-shrink-0">
                <p className="font-medium text-gray-700">{`${hour.toString().padStart(2, '0')}:00`}</p>
              </div>
            ))
          ) : (
            viewDates.map(date => {
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div 
                  key={date.toISOString()} 
                  className={`w-36 p-2 border-r text-center flex-shrink-0 ${isToday ? 'bg-red-50' : 'bg-gray-50'}`}
                >
                  <p className={`text-xs uppercase ${isToday ? 'text-lojack-red font-bold' : 'text-gray-500'}`}>
                    {date.toLocaleDateString(i18n.language, { weekday: 'short' })}
                  </p>
                  <p className={`text-lg font-bold ${isToday ? 'text-lojack-red' : 'text-gray-800'}`}>
                    {date.getDate()}
                  </p>
                  {currentView === 'month' && (
                    <p className="text-xs text-gray-400">
                      {date.toLocaleDateString(i18n.language, { month: 'short' })}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Installer rows */}
        <div className="divide-y">
          {displayedInstallers.map(installer => (
            <div key={installer.id} className="flex hover:bg-gray-50/50">
              {/* Installer info */}
              <div className="w-48 p-3 border-r bg-white flex-shrink-0 sticky left-0 z-[5]">
                <p className="font-semibold text-gray-800 truncate">
                  {installer.first_name} {installer.last_name}
                </p>
                {installer.starting_address && (
                  <p className="text-xs text-gray-500 truncate" title={installer.starting_address}>
                    {installer.starting_address.substring(0, 20)}...
                  </p>
                )}
                <button
                  onClick={() => {
                    const route = calculateDailyRoute(installer, currentDate);
                    if (route) {
                      setRouteData({ installer, ...route, date: currentDate });
                      setShowRouteModal(true);
                    }
                  }}
                  className="mt-1 text-xs text-blue-600 hover:underline"
                >
                  {t('calendar.dailyRoute')} →
                </button>
              </div>

              {/* Appointments cells */}
              {currentView === 'day' ? (
                <div className="relative flex-1" style={{ minWidth: `${12 * 112}px` }}>
                  {/* Hour grid background */}
                  <div className="flex h-full absolute inset-0">
                    {Array.from({ length: 12 }, (_, i) => (
                      <div key={i} className="w-28 border-r border-gray-100 min-h-[70px] flex-shrink-0"></div>
                    ))}
                  </div>
                  
                  {/* Appointments overlay */}
                  {getInstallerAppointments(installer.id, currentDate).map(apt => {
                    const [startHour, startMin] = apt.scheduled_time.split(':').map(Number);
                    const duration = apt.total_duration || apt.estimated_duration || 60;
                    
                    const startOffset = (startHour - 7) * 112 + (startMin / 60) * 112;
                    const width = Math.max((duration / 60) * 112, 80);
                    
                    return (
                      <div
                        key={apt.id}
                        onClick={() => onAppointmentClick(apt)}
                        className="absolute top-1 bottom-1 rounded text-white text-xs cursor-pointer hover:opacity-90 transition-all shadow-sm hover:shadow-md overflow-hidden"
                        style={{ 
                          left: `${startOffset}px`,
                          width: `${width}px`,
                          backgroundColor: getAppointmentColor(apt),
                          border: getAppointmentBorder(apt),
                        }}
                        title={`${apt.scheduled_time} - ${apt.client_name} (${duration} min)`}
                      >
                        <div className="p-1.5 h-full flex flex-col justify-center">
                          <p className="font-bold truncate">{apt.scheduled_time} - {apt.client_name}</p>
                          <p className="opacity-80 truncate text-[10px]">{apt.city}</p>
                          <p className="opacity-70 text-[10px]">
                            {Math.floor(duration / 60) > 0 ? `${Math.floor(duration / 60)}h ` : ''}{duration % 60 > 0 ? `${duration % 60}min` : ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                viewDates.map(date => {
                  const dayApts = getInstallerAppointments(installer.id, date);
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div 
                      key={date.toISOString()} 
                      className={`w-36 p-1 border-r min-h-[80px] flex-shrink-0 ${isToday ? 'bg-red-50/30' : ''}`}
                    >
                      <div className="space-y-1">
                        {dayApts.map(apt => {
                          const duration = apt.total_duration || apt.estimated_duration || 60;
                          const height = Math.min(Math.max((duration / 60) * 28, 28), 80);
                          
                          return (
                            <div 
                              key={apt.id}
                              onClick={() => onAppointmentClick(apt)}
                              className="px-1.5 py-1 rounded text-white text-xs cursor-pointer hover:opacity-80 transition overflow-hidden"
                              style={{ 
                                backgroundColor: getAppointmentColor(apt),
                                border: getAppointmentBorder(apt),
                                minHeight: `${height}px`
                              }}
                              title={`${apt.scheduled_time} - ${apt.client_name} (${duration} min)`}
                            >
                              <span className="font-bold">{apt.scheduled_time}</span>
                              <p className="opacity-90 truncate text-[10px]">{apt.client_name}</p>
                              {duration > 60 && (
                                <p className="opacity-70 text-[9px]">
                                  {Math.floor(duration / 60)}h{duration % 60 > 0 ? ` ${duration % 60}m` : ''}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ROUTE MODAL COMPONENT
// ============================================================================
const RouteModal = ({
  routeData,
  i18n,
  t,
  getStatusColor,
  onClose,
  onAppointmentClick,
  onChangeStartPoint,
}) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">{t('calendar.dailyRoute')}</h3>
            <p className="text-sm text-gray-600">
              {routeData.installer.first_name} {routeData.installer.last_name} - {routeData.date.toLocaleDateString(i18n.language)}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {/* Starting point */}
          <div className="flex items-start gap-3 mb-4 pb-4 border-b">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
              S
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase font-medium">{t('calendar.startingPoint')}</p>
              <p className="text-sm text-gray-800">{routeData.startAddress || 'Non impostato'}</p>
              <button
                onClick={() => onChangeStartPoint(routeData.installer)}
                className="text-xs text-blue-600 hover:underline mt-1"
              >
                {t('calendar.changeStartPoint')}
              </button>
            </div>
          </div>

          {/* Route steps */}
          {routeData.route.map((step, idx) => (
            <div key={idx} className="flex items-start gap-3 mb-4">
              <div className="flex flex-col items-center">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                  style={{ backgroundColor: getStatusColor(step.appointment.status) }}
                >
                  {idx + 1}
                </div>
                {idx < routeData.route.length - 1 && (
                  <div className="w-0.5 h-12 bg-gray-300 my-1"></div>
                )}
              </div>
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-800">{step.appointment.scheduled_time}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: getStatusColor(step.appointment.status) }}>
                    {t(`appointment.statuses.${step.appointment.status}`)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800 mt-1">{step.appointment.client_name}</p>
                <p className="text-xs text-gray-500">{step.to}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {step.appointment.vehicle_brand} {step.appointment.vehicle_model} - {step.appointment.license_plate}
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => onAppointmentClick(step.appointment)}
                    className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    {t('appointment.details')}
                  </button>
                  <button
                    onClick={() => {
                      const fromAddr = idx === 0 ? routeData.startAddress : routeData.route[idx-1].to;
                      window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(fromAddr)}&destination=${encodeURIComponent(step.to)}`, '_blank');
                    }}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    {t('appointment.navigate')} ↗
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Summary */}
          <div className="mt-4 pt-4 border-t bg-gray-50 -mx-4 -mb-4 px-4 py-3">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{routeData.appointments.length}</span> appuntamenti per oggi
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Clicca su &quot;Naviga&quot; per aprire il percorso in Google Maps
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamViewCalendar;
